import { useEffect, useState } from 'react';

interface Timer {
    id: string;
    endTime: number; // timestamp when timer should end
    isActive: boolean;
    onComplete?: () => void;
}

interface TimerHookReturn {
    remainingTime: number;
    isActive: boolean;
    startTimer: (duration: number) => void;
    stopTimer: () => void;
    resetTimer: () => void;
}

// Store timers outside of component lifecycle for persistence
const timersStore: Map<string, Timer> = new Map();

/**
 * Hook to manage persistent timers that survive navigation and component unmounting
 * @param timerId - Unique identifier for the timer
 * @param onComplete - Optional callback to execute when timer completes (will be updated on each render)
 * @returns Timer controls and state
 */
export const useTimers = (timerId: string, onComplete?: () => void): TimerHookReturn => {
    const [remainingTime, setRemainingTime] = useState<number>(0);
    const [isActive, setIsActive] = useState<boolean>(false);

    // Update the callback in the store whenever it changes
    useEffect(() => {
        const storedTimer = timersStore.get(timerId);
        if (storedTimer && onComplete) {
            storedTimer.onComplete = onComplete;
        }
    }, [timerId, onComplete]);

    // Initialize from store on mount
    useEffect(() => {
        const storedTimer = timersStore.get(timerId);
        if (storedTimer && storedTimer.isActive) {
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((storedTimer.endTime - now) / 1000));

            if (remaining > 0) {
                setRemainingTime(remaining);
                setIsActive(true);
            } else {
                // Timer expired while component was unmounted
                if (storedTimer.onComplete) {
                    storedTimer.onComplete();
                }
                timersStore.delete(timerId);
                setRemainingTime(0);
                setIsActive(false);
            }
        }
    }, [timerId]);

    // Update remaining time every second
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isActive && remainingTime > 0) {
            interval = setInterval(() => {
                const storedTimer = timersStore.get(timerId);
                if (!storedTimer) {
                    setIsActive(false);
                    setRemainingTime(0);
                    return;
                }

                const now = Date.now();
                const remaining = Math.max(0, Math.ceil((storedTimer.endTime - now) / 1000));

                if (remaining <= 0) {
                    setRemainingTime(0);
                    setIsActive(false);
                    if (storedTimer.onComplete) {
                        storedTimer.onComplete();
                    }
                    timersStore.delete(timerId);
                } else {
                    setRemainingTime(remaining);
                }
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isActive, remainingTime, timerId]);

    const startTimer = (duration: number) => {
        const endTime = Date.now() + duration * 1000;
        const existingTimer = timersStore.get(timerId);
        timersStore.set(timerId, {
            id: timerId,
            endTime,
            isActive: true,
            onComplete: existingTimer?.onComplete || onComplete
        });
        setRemainingTime(duration);
        setIsActive(true);
    };

    const stopTimer = () => {
        timersStore.delete(timerId);
        setRemainingTime(0);
        setIsActive(false);
    };

    const resetTimer = () => {
        stopTimer();
    };

    return {
        remainingTime,
        isActive,
        startTimer,
        stopTimer,
        resetTimer
    };
};
